import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useSelector } from 'react-redux';
import RouterSideNav from 'ui-addon-react-router-dom/RouterSideNav';
import RouterSideNavItem from 'ui-addon-react-router-dom/RouterSideNavItem';
import { STANDALONE_CORE_SEATS } from '../../../constants/ReleaseInsights';
import { NavItemEnum, SegmentedNavItems } from '../../../constants/routing/NavItems';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useAddOnsPage } from '../../../hooks/useAddOnPage';
import { useHasReleaseInsight } from '../../../hooks/useBulkPurchaseOptions';
import { usePageParams } from '../../../hooks/usePageParams';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForBreezeIntelligenceSelector, isUngatedForMarketingPlusSelector } from '../../../selectors/GatingSelectors';
import { productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { useSSM0009Experiment, useSSM0009HideStarterEffect } from '../../../utils/experiments/ssm0009';
import CurrencyPicker from '../../common/CurrencyPicker';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SideNavWrapper = ({
  children
}) => {
  return /*#__PURE__*/_jsx(View, {
    marginInlineEnd: "900",
    marginBlockEnd: "1300",
    blockSize: "100%",
    children: /*#__PURE__*/_jsx(View, {
      insetBlockStart: "24px",
      children: /*#__PURE__*/_jsx("div", {
        children: children
      })
    })
  });
};
const SideNav = ({
  selectedNavItem
}) => {
  const {
    tab
  } = usePageParams();
  const {
    isSSM0009Variant
  } = useSSM0009Experiment();
  useSSM0009HideStarterEffect(isSSM0009Variant, selectedNavItem);
  const isUngatedToMarketingPlus = useSelector(isUngatedForMarketingPlusSelector);
  const {
    hasReleaseInsight: isSmartCrm,
    loading: isLoadingSmartCrm
  } = useHasReleaseInsight({
    releaseInsight: STANDALONE_CORE_SEATS
  });
  const isAppReady = useGlobalLoading();
  const isUngatedForBreezeIntelligence = useSelector(isUngatedForBreezeIntelligenceSelector);
  const {
    showAddonsPage
  } = useAddOnsPage();
  const isAppAuth = useSelector(isAppAuthSelector);
  const ownedProducts = useSelector(state => {
    if (!isAppReady) return [];
    return productsFromProductInventorySelector(state);
  });
  const isFreePortal = isAppReady && ownedProducts && ownedProducts.length <= 0;
  const shouldHideNavSection = section => {
    return section === 'enhancements' && !showAddonsPage && !isUngatedForBreezeIntelligence;
  };
  const shouldHideNavItem = item => {
    return item === NavItemEnum.MARKETING_PLUS && !isUngatedToMarketingPlus || item === NavItemEnum.ADD_ONS && !showAddonsPage || item === NavItemEnum.BREEZE_INTELLIGENCE && !isUngatedForBreezeIntelligence || item === NavItemEnum.FREE && isAppAuth && !isFreePortal ||
    // don't show free link if in-app pricing page and paid
    item === NavItemEnum.BUNDLE && isAppAuth ||
    // don't show bundle link if in-app pricing page
    item === NavItemEnum.RECOMMENDED ||
    // don't show recommended link if not in SSM0010 variant
    item === NavItemEnum.SMART_CRM && (isLoadingSmartCrm || !isSmartCrm) //don't show smart crm link if not ungated
    ;
  };
  const hasTabs = navItem => {
    return NavItemEnum.BUNDLE !== navItem && NavItemEnum.FREE !== navItem && NavItemEnum.COMMERCE !== navItem && NavItemEnum.MARKETING_PLUS !== navItem && NavItemEnum.ADD_ONS !== navItem && NavItemEnum.BREEZE_INTELLIGENCE !== navItem && NavItemEnum.RECOMMENDED !== navItem;
  };
  const getPath = navItem => {
    const path = `/${navItem}`;
    return hasTabs(navItem) && tab ? `${path}/${tab}` : path;
  };
  const {
    height
  } = useWindowSize();
  return /*#__PURE__*/_jsx(View, {
    position: "sticky",
    insetBlockStart: "24px",
    children: /*#__PURE__*/_jsx(ScrollContainer, {
      id: "scroll-container",
      scrollDirection: "vertical",
      maxHeight: `${height - 150}px`,
      overflowY: "auto",
      children: /*#__PURE__*/_jsxs(SideNavWrapper, {
        children: [Object.keys(SegmentedNavItems).map((navSection, index) => {
          if (shouldHideNavSection(navSection)) return null;
          return /*#__PURE__*/_jsx(RouterSideNav, {
            divider: index !== 0,
            headingText: I18n.text(`pricingPage.sidenav.sections.${navSection}`),
            children: SegmentedNavItems[navSection].map(navItem => {
              if (shouldHideNavItem(navItem)) return null;
              return /*#__PURE__*/_jsx(RouterSideNavItem, {
                "data-test-id": `side-nav-${navItem}`,
                "data-is-active": selectedNavItem === navItem,
                "data-selenium-class": "side-nav",
                active: selectedNavItem === navItem,
                title: /*#__PURE__*/_jsx(Text, {
                  textWrap: "nowrap",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: `pricingPage.pages.products.tabs.${navItem}.tabTitle`
                  })
                }),
                to: createToObject(getPath(navItem)),
                padding: "400",
                marginInlineStart: "300"
              }, navItem);
            })
          }, navSection);
        }), /*#__PURE__*/_jsx(CurrencyPicker, {})]
      })
    })
  });
};
export default SideNav;